// @flow
import _ from 'lodash';
import { normalize, schema } from 'normalizr';
import dotProp from 'dot-prop-immutable';
import type { FluxStandardAction } from 'flux-standard-action';
import * as selectors from './selectors';
import { API_CALL } from '../apiMiddleware';
import type { ExtractReturn } from '../../types';

export const PERMISSION_FETCH = 'gdpr/permissions/PERMISSION_FETCH';
export const PERMISSION_CHANNEL_SET = 'gdpr/permissions/PERMISSION_CHANNEL_SET';
export const PERMISSION_GLOBAL_SET = 'gdpr/permissions/PERMISSION_GLOBAL_SET';

const toggleHistory = [];

const fetchPermissions: FluxStandardAction<> = () => ({
  type: PERMISSION_FETCH,
  meta: {
    [API_CALL]: 'getUserPermissions'
  }
});

const setChannel: FluxStandardAction<> = (permission, channel, value, userPermission) => ({
  type: PERMISSION_CHANNEL_SET,
  payload: [permission, channel, value, userPermission],
  meta: {
    [API_CALL]: 'setUserPermission'
  }
});

const setGlobal: FluxStandardAction<> = (permissionIds, value) => ({
  type: PERMISSION_GLOBAL_SET,
  payload: [permissionIds, value],
  meta: {
    [API_CALL]: 'setGlobalPermission'
  }
});

export type Actions =
  | ExtractReturn<typeof fetchPermissions>
  | ExtractReturn<typeof setPermissionChannel>
  | ExtractReturn<typeof setGlobal>;

export type State = {
  +isFetching?: boolean,
  +channelError?: boolean,
  +error?: boolean,
  +result?: {},
  +isChannelPosting?: boolean,
  +entities?: {}
};

const userPermissionSchema = new schema.Entity('userPermissions', {});
const permissionSchema = new schema.Entity('permissions');
permissionSchema.define({
  children: [permissionSchema],
  userPermissions: [userPermissionSchema]
});

export default function reducer(
  state: State = {},
  action: FluxStandardAction<>
) {
  switch (action.type) {
    case PERMISSION_FETCH:
      return action.error
        ? {
            ...state,
            isFetching: false,
            error: action.error
          }
        : normalize(action.payload.payload, [permissionSchema]);
    case `${PERMISSION_FETCH}_PENDING`:
      return {
        ...state,
        isFetching: true,
        error: false
      };

    case PERMISSION_GLOBAL_SET:
      if (action.error) {
        return {
          ...state,
          isFetching: false,
          error: true
        };
      }

      return {
        ...state,
        isFetching: false,
        error: false
      };
    case `${PERMISSION_CHANNEL_SET}_PENDING`: { // eslint-disable-line
      toggleHistory.push(state);
      const newState = {
        ...dotProp.set(
          state,
          `entities.permissions.${action.meta.args[0].id}.allowed`,
          action.meta.args[1]
            ? {
                ...action.meta.args[0].allowed,
                [action.meta.args[1]]: action.meta.args[2]
              }
            : _.mapValues(
                action.meta.args[0].allowed,
                () => action.meta.args[2]
              )
        ),
        isChannelPosting: true,
        channelError: false
      };
      return newState;
    }
    case PERMISSION_CHANNEL_SET:
      if (action.error) {
        return {
          ...toggleHistory[toggleHistory.length - 1],
          channelError: true,
          isChannelPosting: false
        };
      }

      return {
        ...state,
        isChannelPosting: false,
        channelError: false
      };
    default:
      return state;
  }
}

export const actions = {
  fetchPermissions,
  setChannel,
  setGlobal
};
export { selectors };
